import { checkCellValueStyle, NumericalCellValueStyle } from '@toggle/helpers';
import clsx from 'clsx';
import { startOfDay, subDays } from 'date-fns';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useCatalystInsightsList } from '~/react-query-hooks/insights/useCatalystInsights';
import { useGetV2ThemePerformance } from '~/react-query-hooks/themes/useGetV2ThemePerformance';
import { useGetV4ThemePerformance } from '~/react-query-hooks/themes/useGetV4ThemePerformance';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { useFlags } from '~/stores/use-flags/useFlags';
import { CatalystInsightPill } from '~/widgets/catalyst-insights-drawer/catalyst-insight-pill/CatalystInsightPill';
import { formatPercentageNumber } from '~/widgets/earnings/utils/utils';

import { ThemeLogoIcon } from '../theme-logo-icon/ThemeLogoIcon';
import styles from './ThemeHeader.module.scss';

const numericalStyleToClass: Record<NumericalCellValueStyle, string> = {
  [NumericalCellValueStyle.Positive]: styles.bullish,
  [NumericalCellValueStyle.Negative]: styles.bearish,
  [NumericalCellValueStyle.Neutral]: styles.neutral,
};

interface ThemeHeaderProps {
  themeId: string;
  themeName: string;
  logo_url?: string;
  isCondensed?: boolean;
}

export const ThemeHeader = ({
  themeId,
  themeName,
  logo_url,
  isCondensed = false,
}: ThemeHeaderProps) => {
  const { t } = useTranslation('widget');
  const isNewThemePerformance = useFlags(state =>
    state.getFeatureFlagState('new_theme_performance')
  );

  const fromDate = useMemo(() => startOfDay(subDays(new Date(), 3)), []);
  const { data: catalystInsightsData } = useCatalystInsightsList({
    themes: [themeId],
    categories: ['market_wide'],
    from: fromDate.toISOString(),
    page: 1,
    page_size: 1,
  });

  const { data: v2PerformanceData } = useGetV2ThemePerformance(
    { ids: themeId, intraday: true },
    !isNewThemePerformance
  );

  const { data: v4PerformanceData } = useGetV4ThemePerformance(
    { ids: themeId },
    isNewThemePerformance
  );

  const v2IntradayData = v2PerformanceData?.themes?.[0]?.intraday;
  const v2DisplayValue = v2IntradayData?.current;

  const v4ThemeData = v4PerformanceData?.pages[0].themes[0];
  const performance = v4ThemeData?.performance;
  const headlineHorizon = v4ThemeData?.intraday_metadata.headline_horizon;
  const v4DisplayValue = headlineHorizon
    ? performance?.[headlineHorizon]
    : performance?.['1d'];

  const displayValue = isNewThemePerformance
    ? v4DisplayValue ?? undefined
    : v2DisplayValue;
  const numericalStyle = checkCellValueStyle(displayValue);

  const formattedDate = useMemo(() => {
    const lastUpdated = isNewThemePerformance
      ? v4ThemeData?.intraday_metadata?.last_updated
      : v2IntradayData?.last_updated;

    if (!lastUpdated) {
      return undefined;
    }

    try {
      return new Intl.DateTimeFormat(navigator.language, {
        day: 'numeric',
        month: 'short',
      }).format(new Date(lastUpdated));
    } catch {
      return undefined;
    }
  }, [
    isNewThemePerformance,
    v4ThemeData?.intraday_metadata?.last_updated,
    v2IntradayData?.last_updated,
  ]);

  return (
    <div className={styles.flexRow}>
      <div
        className={clsx(styles.logoAndTitle, {
          [styles.condensed]: isCondensed,
        })}
      >
        <ThemeLogoIcon
          logo_url={logo_url}
          name={themeName}
          key={themeName}
          isCondensed={isCondensed}
        />
        <div
          className={clsx(styles.nameAndReturn, {
            [styles.condensed]: isCondensed,
          })}
        >
          <span
            className={clsx(styles.titleText, {
              [styles.condensed]: isCondensed,
            })}
          >
            {themeName}
          </span>
          {displayValue !== undefined &&
            (isCondensed ? (
              <span
                className={clsx(
                  styles.returnValue,
                  styles.condensed,
                  numericalStyleToClass[numericalStyle]
                )}
              >
                {formatPercentageNumber({
                  value: displayValue,
                  includePercentSign: true,
                  addPlus: true,
                  formatZero: true,
                })}
              </span>
            ) : (
              <div className={styles.returnRow}>
                <span
                  className={clsx(
                    styles.returnValue,
                    numericalStyleToClass[numericalStyle]
                  )}
                >
                  {formatPercentageNumber({
                    value: displayValue,
                    includePercentSign: true,
                    addPlus: true,
                    formatZero: true,
                  })}
                </span>
                {formattedDate && (
                  <span className={styles.returnLabel}>
                    {t('widget:themeOverview.asOfDate', {
                      date: formattedDate,
                    })}
                  </span>
                )}
              </div>
            ))}
        </div>
      </div>
      {!!catalystInsightsData?.length && (
        <CatalystInsightPill
          insightId={catalystInsightsData[0].id}
          themeId={themeId}
          subCategory={catalystInsightsData[0].sub_category}
          isClickable
          source={POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW}
          category={catalystInsightsData[0].category}
        />
      )}
    </div>
  );
};
